import { useCallback, useEffect, useRef, useState } from 'react';
import { compositionFluency } from './baseline';
import { wpm as wpmFn } from './stats';
import {
  isPrintableTypingKey,
  shouldIgnoreKeyEvent,
} from './typingKey';

export interface Story3DraftStats {
  wpm: number;
  fluency: number;
  correctChars: number;
  totalKeystrokes: number;
  durationMs: number;
}

export interface UseStory3DraftInputState {
  text: string;
  stats: Story3DraftStats;
  reset: (initialText?: string) => void;
}

export function useStory3DraftInput(
  enabled: boolean,
  initialText = '',
): UseStory3DraftInputState {
  const [text, setText] = useState(initialText);
  const [tick, setTick] = useState(0);
  const stateRef = useRef({
    text: initialText,
    startedAtMs: 0,
    correctChars: 0,
    totalKeystrokes: 0,
    backspaces: 0,
  });

  const forceRerender = useCallback(() => setTick((t) => t + 1), []);

  const reset = useCallback(
    (nextInitial = '') => {
      stateRef.current = {
        text: nextInitial,
        startedAtMs: 0,
        correctChars: 0,
        totalKeystrokes: 0,
        backspaces: 0,
      };
      setText(nextInitial);
      forceRerender();
    },
    [forceRerender],
  );

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      if (stateRef.current.startedAtMs > 0) forceRerender();
    }, 500);
    return () => window.clearInterval(id);
  }, [enabled, forceRerender]);

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      const s = stateRef.current;
      if (shouldIgnoreKeyEvent(e)) return;
      if (e.key === 'Escape') return;

      if (e.key === 'Enter') {
        e.preventDefault();
        if (s.startedAtMs === 0) s.startedAtMs = Date.now();
        s.totalKeystrokes += 1;
        s.correctChars += 1;
        s.text += '\n';
        setText(s.text);
        forceRerender();
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        if (s.text.length === 0) return;
        s.backspaces += 1;
        s.totalKeystrokes += 1;
        s.text = s.text.slice(0, -1);
        s.correctChars = Math.max(0, s.correctChars - 1);
        setText(s.text);
        forceRerender();
        return;
      }

      if (e.key === 'Dead') return;
      if (!isPrintableTypingKey(e.key)) return;
      e.preventDefault();

      if (s.startedAtMs === 0) s.startedAtMs = Date.now();
      s.totalKeystrokes += 1;
      s.correctChars += 1;
      s.text += e.key;
      setText(s.text);
      forceRerender();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, forceRerender]);

  void tick;
  const s = stateRef.current;
  const durationMs =
    s.startedAtMs === 0 ? 0 : Math.max(1, Date.now() - s.startedAtMs);

  return {
    text,
    stats: {
      wpm: wpmFn(s.correctChars, durationMs),
      fluency: compositionFluency(s.totalKeystrokes, s.backspaces),
      correctChars: s.correctChars,
      totalKeystrokes: s.totalKeystrokes,
      durationMs,
    },
    reset,
  };
}
